import type { NativeInvoiceLabelKey } from "../constants/nativeInvoice";
import type { InvoiceAddressLineLabels } from "./nativeInvoice";
import { normalizeNativeInvoicePdfFieldSettings } from "./normalizeNativeInvoicePdfFieldSettings";
import type { NativeInvoicePdfFieldSettings } from "../schemas/domains/invoice/native-invoice-field-settings.schema";
import type {
    NativeInvoiceAddress,
    NativeInvoiceParty,
} from "../types/nativeInvoice";

// Top-left document heading ("INVOICE" by default, see
// NATIVE_INVOICE_LABEL_DEFAULTS.invoiceHeading). Single toggleable/labelable
// field: unchecking it hides the printed heading text entirely (the rest of
// the masthead - logo/monogram and the info grid - still renders), and a
// custom label overrides the printed text instead of falling back to
// "INVOICE".
export const NATIVE_INVOICE_PDF_HEADER_FIELD_IDS = ["invoiceHeading"] as const;

// Order matches the printed PDF's top-to-bottom party block (see
// buildPartyView/partyColumn in nativeInvoiceDocumentMarkup.ts): name/
// display name, contact name, sender, reply to, address lines (street1/
// street2/city/state/zip/country), then phone. `businessName` ("Display
// name") has no dedicated invoice-form input of its own (it's edited via
// the From entity picker, see DetailsFromFields) but still prints on the
// PDF, so - like the To party's `company` - it needs a settings row like
// every other printed field (label + include-on-PDF toggle) - see
// PRO-12177.
export const NATIVE_INVOICE_PDF_FROM_FIELD_IDS = [
    "from",
    "businessName",
    "fullName",
    "sender",
    "replyTo",
    "street1",
    "street2",
    "city",
    "state",
    "zip",
    "country",
    "phoneNumber",
] as const;

// Order matches the printed PDF's top-to-bottom party block (see
// buildPartyView/partyColumn in nativeInvoiceDocumentMarkup.ts): display
// name, contact name, company, email, address lines (street1/street2/city/
// state/zip/country), then phone. The headline settings id stays `company`
// so saved field settings keep applying; the printed value is the
// counterparty display name (customer.name). `companyName` is the invoice
// Company field, printed on its own line and hidden until included. `email`
// has no dedicated invoice-form input of its own (it's edited via the To
// section's Email field in DetailsToFields) but still prints on the PDF, so
// it needs a settings row like every other printed field (label +
// include-on-PDF toggle) — see PRO-12175.
export const NATIVE_INVOICE_PDF_TO_FIELD_IDS = [
    "to",
    "company",
    "fullName",
    "companyName",
    "email",
    "street1",
    "street2",
    "city",
    "state",
    "zip",
    "country",
    "phoneNumber",
] as const;

// Payment section (PRO-12178): Invoice number, PO number, Terms, Invoice
// date, Due date - in this printed order (matches the invoice PDF's info
// grid - see infoBlock calls in nativeInvoiceDocumentMarkup.ts). `termsType`
// is a newly-printed field: the resolved Terms dropdown label (On receipt /
// Net 30 / etc. - see NativeInvoiceTermType/NATIVE_INVOICE_TERM_TYPE_LABELS)
// had no printed representation before PRO-12178. Distinct from `terms` in
// NATIVE_INVOICE_PDF_NOTES_FIELD_IDS below, which is the free-text "Payment
// instructions" field.
export const NATIVE_INVOICE_PDF_DETAILS_FIELD_IDS = [
    "invoiceNumber",
    "poNumber",
    "termsType",
    "invoiceDate",
    "dueDate",
] as const;

// Notes section (PRO-12178): Memo, Payment instructions - split out of the
// combined "Payment & notes" section into its own group/table so the two
// concepts group separately in the Fields & visibility panel.
export const NATIVE_INVOICE_PDF_NOTES_FIELD_IDS = ["memo", "terms"] as const;

// Items table columns, in print order. tax/discount/deposit are the per-line
// columns, which also require the "Display column per line item" toggle.
export const NATIVE_INVOICE_PDF_ITEMS_TABLE_FIELD_IDS = [
    "item",
    "quantity",
    "rate",
    "tax",
    "discount",
    "deposit",
    "amount",
] as const;

// Totals rows beneath the items table, configured independently of the
// per-line tax/discount/deposit columns above.
export const NATIVE_INVOICE_PDF_TOTALS_FIELD_IDS = [
    "subtotal",
    "tax",
    "discount",
    "deposit",
    "totalDue",
] as const;

const DEFAULT_EXCLUDED_FROM_IDS = ["sender", "replyTo"] as const;
const DEFAULT_EXCLUDED_TO_IDS = ["fullName", "companyName"] as const;
// Terms (the resolved Net 30 / On receipt label) is opt-in: hidden on new
// invoices/templates until the user checks it in the Payment section.
const DEFAULT_EXCLUDED_DETAILS_IDS = ["termsType"] as const;
// Display name stays on the PDF. `company` is the To headline row; `companyName`
// is the separate Company line and is not in this set.
const ALWAYS_PRINTED_FIELD_IDS = new Set<string>(["businessName", "company"]);
// A saved settings blob from before Company existed has no `companyName` key.
// Missing means hidden, matching the default for new invoices.
const HIDDEN_WHEN_UNSET_FIELD_IDS = new Set<string>(["companyName"]);

// Identity map, same rationale as ITEMS_TABLE_LABEL_KEYS below: the header's
// only field id (invoiceHeading) is already a NativeInvoiceLabelKey.
const HEADER_LABEL_KEYS = {
    invoiceHeading: "invoiceHeading",
} as const satisfies Record<
    (typeof NATIVE_INVOICE_PDF_HEADER_FIELD_IDS)[number],
    NativeInvoiceLabelKey
>;

const DETAILS_LABEL_KEYS = {
    invoiceNumber: "invoiceNumber",
    invoiceDate: "invoiceDate",
    dueDate: "dueDate",
    poNumber: "poNumber",
    termsType: "termsType",
} as const satisfies Record<
    (typeof NATIVE_INVOICE_PDF_DETAILS_FIELD_IDS)[number],
    NativeInvoiceLabelKey
>;

const NOTES_LABEL_KEYS = {
    terms: "paymentInstructions",
    memo: "memo",
} as const satisfies Record<
    (typeof NATIVE_INVOICE_PDF_NOTES_FIELD_IDS)[number],
    NativeInvoiceLabelKey
>;

const ITEMS_TABLE_LABEL_KEYS = {
    item: "item",
    quantity: "quantity",
    rate: "rate",
    tax: "taxColumn",
    discount: "discountColumn",
    deposit: "depositColumn",
    amount: "amount",
} as const satisfies Record<
    (typeof NATIVE_INVOICE_PDF_ITEMS_TABLE_FIELD_IDS)[number],
    NativeInvoiceLabelKey
>;

const TOTALS_LABEL_KEYS = {
    subtotal: "subtotal",
    tax: "tax",
    discount: "discount",
    deposit: "deposit",
    totalDue: "totalDue",
} as const satisfies Record<
    (typeof NATIVE_INVOICE_PDF_TOTALS_FIELD_IDS)[number],
    NativeInvoiceLabelKey
>;

function groupFromIds(
    fieldIds: readonly string[],
    excludedIds: readonly string[] = [],
): Record<string, { label: string; isIncludedOnPdf: boolean }> {
    return Object.fromEntries(
        fieldIds.map((fieldId) => [
            fieldId,
            { label: "", isIncludedOnPdf: !excludedIds.includes(fieldId) },
        ]),
    );
}

export function createDefaultNativeInvoicePdfFieldSettings(): NativeInvoicePdfFieldSettings {
    return {
        header: groupFromIds(NATIVE_INVOICE_PDF_HEADER_FIELD_IDS),
        from: groupFromIds(
            NATIVE_INVOICE_PDF_FROM_FIELD_IDS,
            DEFAULT_EXCLUDED_FROM_IDS,
        ),
        to: groupFromIds(
            NATIVE_INVOICE_PDF_TO_FIELD_IDS,
            DEFAULT_EXCLUDED_TO_IDS,
        ),
        details: groupFromIds(
            NATIVE_INVOICE_PDF_DETAILS_FIELD_IDS,
            DEFAULT_EXCLUDED_DETAILS_IDS,
        ),
        notes: groupFromIds(NATIVE_INVOICE_PDF_NOTES_FIELD_IDS),
        itemsTable: groupFromIds(NATIVE_INVOICE_PDF_ITEMS_TABLE_FIELD_IDS),
        totals: groupFromIds(NATIVE_INVOICE_PDF_TOTALS_FIELD_IDS),
    };
}

export function createAllIncludedNativeInvoicePdfFieldSettings(): NativeInvoicePdfFieldSettings {
    return {
        header: groupFromIds(NATIVE_INVOICE_PDF_HEADER_FIELD_IDS),
        from: groupFromIds(NATIVE_INVOICE_PDF_FROM_FIELD_IDS),
        to: groupFromIds(NATIVE_INVOICE_PDF_TO_FIELD_IDS, ["companyName"]),
        details: groupFromIds(NATIVE_INVOICE_PDF_DETAILS_FIELD_IDS),
        notes: groupFromIds(NATIVE_INVOICE_PDF_NOTES_FIELD_IDS),
        itemsTable: groupFromIds(NATIVE_INVOICE_PDF_ITEMS_TABLE_FIELD_IDS),
        totals: groupFromIds(NATIVE_INVOICE_PDF_TOTALS_FIELD_IDS),
    };
}

export function isNativeInvoicePdfFieldIncluded(
    settings: Record<string, { isIncludedOnPdf: boolean }> | undefined,
    fieldId: string,
): boolean {
    const setting = settings?.[fieldId];
    if (!setting) {
        return !HIDDEN_WHEN_UNSET_FIELD_IDS.has(fieldId);
    }
    return setting.isIncludedOnPdf;
}

function isFieldPrinted(
    settings: Record<string, { isIncludedOnPdf: boolean }> | undefined,
    fieldId: string,
): boolean {
    if (ALWAYS_PRINTED_FIELD_IDS.has(fieldId)) {
        return true;
    }
    return isNativeInvoicePdfFieldIncluded(settings, fieldId);
}

export function getNativeInvoicePdfFieldCustomLabel(
    settings: Record<string, { label: string }> | undefined,
    fieldId: string,
): string {
    return settings?.[fieldId]?.label.trim() ?? "";
}

// The custom label for a field, but only when that field is actually included
// on the PDF. A hidden field contributes no rendered line, so its label must
// not leak into address grouping (see maskNativeInvoiceAddressBySettings) where
// a stray label would split visible siblings out of the merged city/state/zip
// line.
function getIncludedNativeInvoicePdfFieldCustomLabel(
    settings:
        | Record<string, { isIncludedOnPdf: boolean; label: string }>
        | undefined,
    fieldId: string,
): string {
    return isNativeInvoicePdfFieldIncluded(settings, fieldId)
        ? getNativeInvoicePdfFieldCustomLabel(settings, fieldId)
        : "";
}

interface FormatNativeInvoicePdfFieldLineParams {
    settings: Record<string, { label: string }> | undefined;
    fieldId: string;
    value: string;
}

export function formatNativeInvoicePdfFieldLine({
    settings,
    fieldId,
    value,
}: FormatNativeInvoicePdfFieldLineParams): string {
    const label = getNativeInvoicePdfFieldCustomLabel(settings, fieldId);
    return label ? `${label} ${value}` : value;
}

interface VisibleOrNullParams<T> {
    settings: Record<string, { isIncludedOnPdf: boolean }> | undefined;
    fieldId: string;
    value: T | null | undefined;
}

function visibleOrNull<T>({
    settings,
    fieldId,
    value,
}: VisibleOrNullParams<T>): T | null {
    return isNativeInvoicePdfFieldIncluded(settings, fieldId)
        ? (value ?? null)
        : null;
}

interface VisibleTextParams {
    settings:
        | Record<string, { isIncludedOnPdf: boolean; label: string }>
        | undefined;
    fieldId: string;
    value: string | null | undefined;
}

function visibleText({ settings, fieldId, value }: VisibleTextParams): string {
    if (!isFieldPrinted(settings, fieldId) || !value) {
        return "";
    }
    return formatNativeInvoicePdfFieldLine({ settings, fieldId, value });
}

// Same visibility + label-prefix behavior as `visibleText`, but preserves the
// null-when-hidden-or-empty contract that address/party string fields use
// elsewhere in this module (e.g. `visibleOrNull`), rather than `""`. Used for
// fields that render as their own standalone line (fullName, phoneNumber,
// company, street1, street2) - never for fields whose value later gets
// merged with siblings into a single combined line (city/state/zip/country),
// where prefixing a label before the merge would garble the output.
function visibleTextOrNull({
    settings,
    fieldId,
    value,
}: VisibleTextParams): string | null {
    return visibleText({ settings, fieldId, value }) || null;
}

interface ResolvePartyNameLineParams {
    settings:
        | Record<string, { isIncludedOnPdf: boolean; label: string }>
        | undefined;
    businessName: string | null;
    rawBusinessName: string | null | undefined;
    rawName: string | null | undefined;
}

// Resolves a party's standalone name line ("Full name") and dedupes it against
// the business-name line. buildPartyView drops the standalone name when
// businessName and name share the same raw value, but a custom label on the
// business-name field prefixes its rendered text and breaks that raw-value
// comparison, which would print the name twice - so we dedupe here against the
// raw values instead. The name line is kept when it carries its own custom
// label, since a labeled line is a distinct row the user explicitly configured
// to show even if its value matches the business name. Shared by the From
// (businessName/fullName) and To (company/fullName) parties.
function resolvePartyNameLine({
    settings,
    businessName,
    rawBusinessName,
    rawName,
}: ResolvePartyNameLineParams): string | null {
    const hasCustomNameLabel =
        getNativeInvoicePdfFieldCustomLabel(settings, "fullName") !== "";
    const isDuplicateOfBusinessName =
        businessName != null &&
        rawBusinessName != null &&
        rawBusinessName === rawName &&
        !hasCustomNameLabel;
    return isDuplicateOfBusinessName
        ? null
        : visibleTextOrNull({ settings, fieldId: "fullName", value: rawName });
}

interface MaskNativeInvoiceAddressBySettingsParams {
    address: NativeInvoiceAddress | null | undefined;
    settings:
        | Record<string, { isIncludedOnPdf: boolean; label: string }>
        | undefined;
}

export interface MaskNativeInvoiceAddressBySettingsResult {
    address: NativeInvoiceAddress;
    // Custom labels for city/state/zip/country, forwarded to
    // getInvoiceAddressLines so it can break a labeled field out of the
    // merged "Austin, TX 78701"-style line into its own labeled line. Only
    // labels for fields actually included on the PDF are surfaced here: a
    // hidden field renders no line, so its label must not activate the
    // split-address branch and pull visible siblings out of the merged line.
    addressLabels: InvoiceAddressLineLabels;
}

export function maskNativeInvoiceAddressBySettings({
    address,
    settings,
}: MaskNativeInvoiceAddressBySettingsParams): MaskNativeInvoiceAddressBySettingsResult {
    return {
        address: {
            // street/street2 always render as their own dedicated line (see
            // getInvoiceAddressLines), so a custom label can be prefixed safely -
            // same pattern as sender/replyTo.
            street: visibleTextOrNull({
                settings,
                fieldId: "street1",
                value: address?.street,
            }),
            street2: visibleTextOrNull({
                settings,
                fieldId: "street2",
                value: address?.street2,
            }),
            // city/state/postalCode are collapsed into a single combined line by
            // getInvoiceAddressLines (e.g. "Austin, TX 78701") when none of them
            // has a custom label; the moment one does, getInvoiceAddressLines
            // breaks the whole group into individually labeled lines using
            // `addressLabels` below. country always renders as its own line
            // (bare or labeled) after display-name resolution.
            city: visibleOrNull({
                settings,
                fieldId: "city",
                value: address?.city,
            }),
            state: visibleOrNull({
                settings,
                fieldId: "state",
                value: address?.state,
            }),
            postalCode: visibleOrNull({
                settings,
                fieldId: "zip",
                value: address?.postalCode,
            }),
            country: visibleOrNull({
                settings,
                fieldId: "country",
                value: address?.country,
            }),
        },
        addressLabels: {
            city: getIncludedNativeInvoicePdfFieldCustomLabel(settings, "city"),
            state: getIncludedNativeInvoicePdfFieldCustomLabel(
                settings,
                "state",
            ),
            postalCode: getIncludedNativeInvoicePdfFieldCustomLabel(
                settings,
                "zip",
            ),
            country: getIncludedNativeInvoicePdfFieldCustomLabel(
                settings,
                "country",
            ),
        },
    };
}

function customLabelOverrides(
    fieldSettings: NativeInvoicePdfFieldSettings,
): Partial<Record<NativeInvoiceLabelKey, string>> {
    const overrides: Partial<Record<NativeInvoiceLabelKey, string>> = {};
    const fromLabel = getNativeInvoicePdfFieldCustomLabel(
        fieldSettings.from,
        "from",
    );
    if (fromLabel) {
        overrides.from = fromLabel;
    }
    const toLabel = getNativeInvoicePdfFieldCustomLabel(fieldSettings.to, "to");
    if (toLabel) {
        overrides.to = toLabel;
    }
    for (const [fieldId, labelKey] of Object.entries(DETAILS_LABEL_KEYS)) {
        const label = getNativeInvoicePdfFieldCustomLabel(
            fieldSettings.details,
            fieldId,
        );
        if (label) {
            overrides[labelKey] = label;
        }
    }
    for (const [fieldId, labelKey] of Object.entries(NOTES_LABEL_KEYS)) {
        const label = getNativeInvoicePdfFieldCustomLabel(
            fieldSettings.notes,
            fieldId,
        );
        if (label) {
            overrides[labelKey] = label;
        }
    }
    for (const [fieldId, labelKey] of Object.entries(ITEMS_TABLE_LABEL_KEYS)) {
        const label = getNativeInvoicePdfFieldCustomLabel(
            fieldSettings.itemsTable,
            fieldId,
        );
        if (label) {
            overrides[labelKey] = label;
        }
    }
    for (const [fieldId, labelKey] of Object.entries(TOTALS_LABEL_KEYS)) {
        const label = getNativeInvoicePdfFieldCustomLabel(
            fieldSettings.totals,
            fieldId,
        );
        if (label) {
            overrides[labelKey] = label;
        }
    }
    return overrides;
}

// The header group, once materialized in a snapshot, is the single source of
// truth for its label keys: a non-empty field label overrides the default, and
// a cleared (empty) label drops any legacy labelOverrides entry so the default
// heading is restored. Without this, a heading override persisted before the
// header field existed (e.g. set via SQL) would be pinned forever - clearing
// the new Heading input only blanks the field label, which
// customLabelOverrides omits, leaving the stale legacy override to win the
// merge below. A snapshot predating the header group (its field absent, group
// defaulted to {}) leaves the legacy override untouched.
function resolveHeaderLabelOverrides(
    labelOverrides: Partial<Record<NativeInvoiceLabelKey, string>>,
    headerSettings: NativeInvoicePdfFieldSettings["header"] | undefined,
): Partial<Record<NativeInvoiceLabelKey, string>> {
    const resolved = { ...labelOverrides };
    for (const [fieldId, labelKey] of Object.entries(HEADER_LABEL_KEYS)) {
        const headerField = headerSettings?.[fieldId];
        if (!headerField) {
            continue;
        }
        const label = headerField.label.trim();
        if (label) {
            resolved[labelKey] = label;
        } else {
            delete resolved[labelKey];
        }
    }
    return resolved;
}

// Per-field include-on-PDF state for the items table columns and the totals
// rows beneath it. Consumed by the renderer to hide a table column entirely
// (header + every line's cell) or a totals row, in addition to the existing
// value-based auto-visibility (see getInvoiceTotalsVisibility) - a totals row
// only renders when BOTH are true, so hiding a zero-value row is unaffected
// and a non-zero row can still be force-hidden by unchecking it here.
export interface NativeInvoiceItemsTableVisibility {
    item: boolean;
    quantity: boolean;
    rate: boolean;
    amount: boolean;
    // Totals rows, driven by the Totals group.
    subtotal: boolean;
    tax: boolean;
    discount: boolean;
    deposit: boolean;
    totalDue: boolean;
    // Per-line Tax/Discount/Deposit columns, driven by the Items group
    // AND the "Display column per line item" toggle (isXDisplayedPerLineItem)
    // AND the matching calculation flag (isXPerLineItem) — a value only exists
    // per line once calculation is per-line, so the column is force-hidden
    // when calculation is still header-level even if a stale display flag was
    // persisted before calculation was turned off.
    taxColumn: boolean;
    discountColumn: boolean;
    depositColumn: boolean;
}

interface ItemsTableVisibilityInput {
    itemsTable: NativeInvoicePdfFieldSettings["itemsTable"] | undefined;
    totals: NativeInvoicePdfFieldSettings["totals"] | undefined;
    isTaxPerLineItem?: boolean;
    isDiscountPerLineItem?: boolean;
    isDepositPerLineItem?: boolean;
    isTaxDisplayedPerLineItem?: boolean;
    isDiscountDisplayedPerLineItem?: boolean;
    isDepositDisplayedPerLineItem?: boolean;
}

function itemsTableVisibility({
    itemsTable,
    totals,
    isTaxPerLineItem,
    isDiscountPerLineItem,
    isDepositPerLineItem,
    isTaxDisplayedPerLineItem,
    isDiscountDisplayedPerLineItem,
    isDepositDisplayedPerLineItem,
}: ItemsTableVisibilityInput): NativeInvoiceItemsTableVisibility {
    return {
        item: isNativeInvoicePdfFieldIncluded(itemsTable, "item"),
        quantity: isNativeInvoicePdfFieldIncluded(itemsTable, "quantity"),
        rate: isNativeInvoicePdfFieldIncluded(itemsTable, "rate"),
        amount: isNativeInvoicePdfFieldIncluded(itemsTable, "amount"),
        subtotal: isNativeInvoicePdfFieldIncluded(totals, "subtotal"),
        tax: isNativeInvoicePdfFieldIncluded(totals, "tax"),
        discount: isNativeInvoicePdfFieldIncluded(totals, "discount"),
        deposit: isNativeInvoicePdfFieldIncluded(totals, "deposit"),
        totalDue: isNativeInvoicePdfFieldIncluded(totals, "totalDue"),
        taxColumn:
            Boolean(isTaxDisplayedPerLineItem) &&
            Boolean(isTaxPerLineItem) &&
            isNativeInvoicePdfFieldIncluded(itemsTable, "tax"),
        discountColumn:
            Boolean(isDiscountDisplayedPerLineItem) &&
            Boolean(isDiscountPerLineItem) &&
            isNativeInvoicePdfFieldIncluded(itemsTable, "discount"),
        depositColumn:
            Boolean(isDepositDisplayedPerLineItem) &&
            Boolean(isDepositPerLineItem) &&
            isNativeInvoicePdfFieldIncluded(itemsTable, "deposit"),
    };
}

export interface NativeInvoicePdfFieldSettingsApplyInput {
    invoiceNumber?: string | null;
    invoiceDate?: string | null;
    dueDate?: string | null;
    poNumber?: string | null;
    // Resolved Terms dropdown display string (e.g. "Net 30", "Due in 45 days")
    // - callers resolve it from the persisted term rule via
    // invoice-terms.helper.ts#resolveNativeInvoiceTermLabel before calling in,
    // since this module has no access to the term-type enum/labels.
    termTypeLabel?: string | null;
    terms?: string | null;
    note?: string | null;
    from: NativeInvoiceParty;
    to: NativeInvoiceParty;
    labelOverrides?: Partial<Record<NativeInvoiceLabelKey, string>> | null;
    fieldSettings?: NativeInvoicePdfFieldSettings | null;
    senderEmail?: string | null;
    // Per-line calculation + "display column" toggles (see
    // NativeInvoiceItemsTableVisibility.taxColumn/discountColumn/
    // depositColumn).
    isTaxPerLineItem?: boolean;
    isDiscountPerLineItem?: boolean;
    isDepositPerLineItem?: boolean;
    isTaxDisplayedPerLineItem?: boolean;
    isDiscountDisplayedPerLineItem?: boolean;
    isDepositDisplayedPerLineItem?: boolean;
}

export interface NativeInvoicePdfFieldSettingsApplyResult {
    invoiceNumber: string | null;
    invoiceDate: string | null;
    dueDate: string | null;
    poNumber: string | null;
    termTypeLabel: string | null;
    terms: string | null;
    note: string | null;
    from: NativeInvoiceParty;
    to: NativeInvoiceParty;
    // Custom labels for the from/to address rows that merge into a single
    // combined line (city/state/zip/country); forwarded to
    // getInvoiceAddressLines so it can break the merged line apart when one
    // of these fields has a custom label. See maskNativeInvoiceAddressBySettings.
    fromAddressLabels: InvoiceAddressLineLabels;
    toAddressLabels: InvoiceAddressLineLabels;
    // Whether the top-left "INVOICE" heading text renders at all (the rest of
    // the masthead - logo/monogram, info grid - is unaffected). Driven by
    // fieldSettings.header.invoiceHeading.isIncludedOnPdf.
    showHeading: boolean;
    showFromLabel: boolean;
    showToLabel: boolean;
    sender: string;
    labelOverrides: Partial<Record<NativeInvoiceLabelKey, string>> | null;
    itemsTableVisibility: NativeInvoiceItemsTableVisibility;
}

export function applyNativeInvoicePdfFieldSettings(
    input: NativeInvoicePdfFieldSettingsApplyInput,
): NativeInvoicePdfFieldSettingsApplyResult {
    if (!input.fieldSettings) {
        return {
            invoiceNumber: input.invoiceNumber ?? null,
            invoiceDate: input.invoiceDate ?? null,
            dueDate: input.dueDate ?? null,
            poNumber: input.poNumber ?? null,
            termTypeLabel: input.termTypeLabel ?? null,
            terms: input.terms ?? null,
            note: input.note ?? null,
            from: input.from,
            to: {
                ...input.to,
                company: null,
            },
            fromAddressLabels: {},
            toAddressLabels: {},
            showHeading: true,
            showFromLabel: true,
            showToLabel: true,
            sender: "",
            labelOverrides: input.labelOverrides ?? null,
            itemsTableVisibility: itemsTableVisibility({
                ...input,
                itemsTable: undefined,
                totals: undefined,
            }),
        };
    }

    const fieldSettings = normalizeNativeInvoicePdfFieldSettings(
        input.fieldSettings,
    );

    const fromSettings = fieldSettings.from;
    const toSettings = fieldSettings.to;
    const detailsSettings = fieldSettings.details;
    const notesSettings = fieldSettings.notes;

    const fromBusinessName = visibleTextOrNull({
        settings: fromSettings,
        fieldId: "businessName",
        value: input.from.businessName,
    });
    const fromName = resolvePartyNameLine({
        settings: fromSettings,
        businessName: fromBusinessName,
        rawBusinessName: input.from.businessName,
        rawName: input.from.name,
    });

    const toBusinessName = visibleTextOrNull({
        settings: toSettings,
        fieldId: "company",
        value: input.to.businessName,
    });
    const toName = resolvePartyNameLine({
        settings: toSettings,
        businessName: toBusinessName,
        rawBusinessName: input.to.businessName,
        rawName: input.to.name,
    });
    const toCompany = visibleTextOrNull({
        settings: toSettings,
        fieldId: "companyName",
        value: input.to.company,
    });

    const fromAddress = maskNativeInvoiceAddressBySettings({
        address: input.from.address,
        settings: fromSettings,
    });
    const toAddress = maskNativeInvoiceAddressBySettings({
        address: input.to.address,
        settings: toSettings,
    });

    return {
        invoiceNumber: visibleOrNull({
            settings: detailsSettings,
            fieldId: "invoiceNumber",
            value: input.invoiceNumber,
        }),
        invoiceDate: visibleOrNull({
            settings: detailsSettings,
            fieldId: "invoiceDate",
            value: input.invoiceDate,
        }),
        dueDate: visibleOrNull({
            settings: detailsSettings,
            fieldId: "dueDate",
            value: input.dueDate,
        }),
        poNumber: visibleOrNull({
            settings: detailsSettings,
            fieldId: "poNumber",
            value: input.poNumber,
        }),
        termTypeLabel: visibleOrNull({
            settings: detailsSettings,
            fieldId: "termsType",
            value: input.termTypeLabel,
        }),
        terms: visibleOrNull({
            settings: notesSettings,
            fieldId: "terms",
            value: input.terms,
        }),
        note: visibleOrNull({
            settings: notesSettings,
            fieldId: "memo",
            value: input.note,
        }),
        from: {
            ...input.from,
            businessName: fromBusinessName,
            name: fromName,
            email:
                visibleText({
                    settings: fromSettings,
                    fieldId: "replyTo",
                    value: input.from.email,
                }) || null,
            phoneNumber: visibleTextOrNull({
                settings: fromSettings,
                fieldId: "phoneNumber",
                value: input.from.phoneNumber,
            }),
            address: fromAddress.address,
        },
        to: {
            ...input.to,
            businessName: toBusinessName,
            company: toCompany,
            name: toName,
            // The To party's email has no dedicated invoice-form input (it's
            // edited via the customer's Email field in DetailsToFields) but
            // still prints on the PDF, so - like the From party's replyTo -
            // it needs the same label/visibility gating as every other
            // printed To field (see PRO-12175). Previously this value passed
            // through unmasked via the `...input.to` spread above.
            email:
                visibleText({
                    settings: toSettings,
                    fieldId: "email",
                    value: input.to.email,
                }) || null,
            phoneNumber: visibleTextOrNull({
                settings: toSettings,
                fieldId: "phoneNumber",
                value: input.to.phoneNumber,
            }),
            address: toAddress.address,
        },
        fromAddressLabels: fromAddress.addressLabels,
        toAddressLabels: toAddress.addressLabels,
        showHeading: isNativeInvoicePdfFieldIncluded(
            fieldSettings.header,
            "invoiceHeading",
        ),
        showFromLabel: isNativeInvoicePdfFieldIncluded(fromSettings, "from"),
        showToLabel: isNativeInvoicePdfFieldIncluded(toSettings, "to"),
        sender: visibleText({
            settings: fromSettings,
            fieldId: "sender",
            value: input.senderEmail,
        }),
        labelOverrides: resolveHeaderLabelOverrides(
            {
                ...input.labelOverrides,
                ...customLabelOverrides(fieldSettings),
            },
            fieldSettings.header,
        ),
        itemsTableVisibility: itemsTableVisibility({
            ...input,
            itemsTable: fieldSettings.itemsTable,
            totals: fieldSettings.totals,
        }),
    };
}
